// src/pages/QAPage.js
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio as MuiRadio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { CheckCircle, Cancel, HourglassEmpty } from '@mui/icons-material';
import CustomEditor from '../Components/CustomEditor';
import renderMathInElement from 'katex/contrib/auto-render/auto-render';
import 'katex/dist/katex.min.css';

function QAPage() {
  const navigate = useNavigate();

  // States
  const [subject, setSubject] = useState('All');
  const [questions, setQuestions] = useState([]);
  const [approvedCount, setApprovedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modals & dialogs
  const [viewOpen, setViewOpen] = useState(false);
  const [viewedQuestion, setViewedQuestion] = useState(null);
  const [openSubModal, setOpenSubModal] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackQuestion, setSendBackQuestion] = useState(null);
  const [sendBackComment, setSendBackComment] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState('');
  const [editSubQuestions, setEditSubQuestions] = useState([]);
  const [editParentOptions, setEditParentOptions] = useState([]);
  const [editParentCorrectAnswer, setEditParentCorrectAnswer] = useState('');
  const [editSubModalOpen, setEditSubModalOpen] = useState(false);
  const [subQuestionTempEdit, setSubQuestionTempEdit] = useState({
    question: '',
    options: ['', '', '', ''],
    correctOptionIndex: 0,
  });
  const [subQuestionEditResetCounter, setSubQuestionEditResetCounter] = useState(0);

  useEffect(() => {
    fetchQAQuestions();
    fetchQAStats();
  }, []);

  const fetchQAQuestions = async () => {
    try {
      setError(''); setSuccess('');
      const res = await api.get('/api/questions/forQA');
      setQuestions(res.data.map(q => ({ ...q, qaDecision: 'none' })));
    } catch {
      setError('Failed to fetch QA questions.');
    }
  };

  const fetchQAStats = async () => {
    try {
      const res = await api.get('/api/questions/qaStats');
      setApprovedCount(res.data.approved);
      setRejectedCount(res.data.rejected);
      setPendingCount(res.data.pending);
    } catch {
      setError('Failed to fetch QA stats.');
    }
  };

  const markApproved = id => {
    setQuestions(qs => qs.map(q => q._id === id ? { ...q, qaDecision: 'approved' } : q));
  };
  const markRejected = id => {
    setQuestions(qs => qs.map(q => q._id === id ? { ...q, qaDecision: 'rejected' } : q));
  };
  const markNoDecision = id => {
    setQuestions(qs => qs.map(q => q._id === id ? { ...q, qaDecision: 'none' } : q));
  };

  const handleSaveAll = async () => {
    try {
      setError(''); setSuccess('');
      const reviews = questions
        .filter(q => q.qaDecision !== 'none')
        .map(q => ({ questionId: q._id, status: q.qaDecision }));
      if (!reviews.length) {
        setError('No questions were marked.');
        return;
      }
      await api.post('/api/questions/qaReview', { reviews });
      setSuccess('All decisions saved!');
      setQuestions(questions.filter(q => q.qaDecision === 'none'));
      fetchQAStats();
    } catch {
      setError('Failed to save QA decisions.');
    }
  };

  const handleView = q => {
    setViewedQuestion(q);
    setViewOpen(true);
  };
  const handleCloseView = () => {
    setViewedQuestion(null);
    setViewOpen(false);
    setOpenSubModal(false);
  };

  const handleSendBackClick = q => {
    setSendBackQuestion(q);
    setSendBackComment('');
    setSendBackOpen(true);
  };
  const handleSendBackConfirm = async () => {
    if (!sendBackQuestion) return;
    try {
      setError(''); setSuccess('');
      await api.post(`/api/questions/sendBack/${sendBackQuestion._id}`, {
        comment: sendBackComment,
      });
      setSuccess('Question sent back for review.');
      setQuestions(qs => qs.filter(q => q._id !== sendBackQuestion._id));
      setSendBackOpen(false);
      setSendBackQuestion(null);
      fetchQAStats();
    } catch {
      setError('Failed to send question back.');
    }
  };
  const handleSendBackClose = () => {
    setSendBackOpen(false);
    setSendBackQuestion(null);
    setSendBackComment('');
  };

  const handleEditClick = q => {
    setEditQuestion(q);
    setEditQuestionText(q.question);
    setEditSubQuestions(q.subQuestions || []);
    setEditParentOptions(q.options || []);
    setEditParentCorrectAnswer(q.correctAnswer || '');
    setEditOpen(true);
  };
  const handleEditSave = async () => {
    if (!editQuestion) return;
    try {
      setError(''); setSuccess('');
      // include options and correctAnswer when editing a parent question
      const payload = {
        question: editQuestionText,
        subQuestions: editSubQuestions,
      };
      if (!editSubQuestions || editSubQuestions.length === 0) {
        payload.options = editParentOptions;
        payload.correctAnswer = editParentCorrectAnswer;
      }
      await api.put(`/api/questions/qaEdit/${editQuestion._id}`, payload);
      setSuccess('Question updated');
      setQuestions(qs => qs.map(q =>
        q._id === editQuestion._id
          ? { ...q, question: editQuestionText, subQuestions: editSubQuestions, options: editParentOptions, correctAnswer: editParentCorrectAnswer }
          : q
      ));
      setEditOpen(false);
      setEditQuestion(null);
      setEditQuestionText('');
      setEditSubQuestions([]);
      setEditParentOptions([]);
      setEditParentCorrectAnswer('');
    } catch {
      setError('Failed to edit question.');
    }
  };
  const handleEditClose = () => {
    setEditOpen(false);
    setEditQuestion(null);
    setEditQuestionText('');
    setEditSubQuestions([]);
    setEditParentOptions([]);
    setEditParentCorrectAnswer('');
  };

  const handleAddSubQuestionEdit = () => {
    if (
      !subQuestionTempEdit.question.trim() ||
      subQuestionTempEdit.options.some(o => !o.trim())
    ) {
      setError('All sub question fields must be filled.');
      return;
    }
    const newSub = {
      question: subQuestionTempEdit.question,
      options: subQuestionTempEdit.options,
      correctAnswer: subQuestionTempEdit.options[subQuestionTempEdit.correctOptionIndex],
    };
    setEditSubQuestions(prev => [...prev, newSub]);
    setEditSubModalOpen(false);
    setSubQuestionTempEdit({ question: '', options: ['', '', '', ''], correctOptionIndex: 0 });
    setSubQuestionEditResetCounter(c => c + 1);
  };

  const filteredQuestions = questions.filter(q =>
    subject === 'All' ? true : q.subject === subject
  );

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        pb: 3,
        borderBottom: '3px solid',
        borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1',
      }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5 }}>
            QA Portal
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
            Review and approve submitted questions
          </Typography>
        </Box>
        <Button 
          variant="outlined"
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.2,
            fontWeight: 600,
            borderWidth: 2,
            borderColor: '#dc3545',
            color: '#dc3545',
            '&:hover': {
              borderWidth: 2,
              borderColor: '#c82333',
              background: '#fff5f5',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Logout
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Box sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(79, 172, 254, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 28px rgba(79, 172, 254, 0.4)',
            },
          }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: 0.5 }}>
                    Approved
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {approvedCount}
                  </Typography>
                </Box>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <CheckCircle sx={{ fontSize: 40 }} />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Questions approved
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(250, 112, 154, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 28px rgba(250, 112, 154, 0.4)',
            },
          }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: 0.5 }}>
                    Rejected
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {rejectedCount}
                  </Typography>
                </Box>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Cancel sx={{ fontSize: 40 }} />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Questions rejected
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(240, 147, 251, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 28px rgba(240, 147, 251, 0.4)',
            },
          }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: 0.5 }}>
                    Pending
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {pendingCount}
                  </Typography>
                </Box>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <HourglassEmpty sx={{ fontSize: 40 }} />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Awaiting review
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Subject Filter */}
      <Box sx={{ 
        bgcolor: 'white',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        p: 3,
        mb: 3,
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
          Filter Questions
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Filter by Subject</InputLabel>
          <Select 
            value={subject} 
            label="Filter by Subject" 
            onChange={e => setSubject(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          >
          <MenuItem value="All">All</MenuItem>
           <MenuItem value="Understanding of Holy Quran">Understanding of Holy Quran</MenuItem>
          <MenuItem value="Mathematics">Mathematics</MenuItem>
           <MenuItem value="Philosophy">Philosophy</MenuItem>
            <MenuItem value="Philosophy 1">Philosophy 1</MenuItem>
          <MenuItem value="English">English</MenuItem>
          <MenuItem value="Computer">Computer</MenuItem>
           <MenuItem value="Computer Science">Computer Science</MenuItem>
            <MenuItem value="IT">IT</MenuItem>

         
          
          <MenuItem value="Physics">Physics</MenuItem>
          <MenuItem value="Chemistry">Chemistry</MenuItem>
          <MenuItem value="General Knowledge">General Knowledge</MenuItem>
          <MenuItem value="General Science">General Science</MenuItem>
          <MenuItem value="Islamiat">Islamiat</MenuItem>
          <MenuItem value="Medical">Medical</MenuItem>
          <MenuItem value="Ethics">Ethics</MenuItem>
          <MenuItem value="Education">Education</MenuItem>
          <MenuItem value="Business Administration">Business Administration</MenuItem>
          <MenuItem value="Commerce">Commerce</MenuItem>
          <MenuItem value="Research/ORIC">Research/ORIC</MenuItem>
          <MenuItem value="Finance">Finance</MenuItem>
          <MenuItem value="Examinations">Examinations</MenuItem>
          <MenuItem value="Civil">Civil</MenuItem>
          <MenuItem value="Electrical Engineering">Electrical Engineering</MenuItem>
          <MenuItem value="Security">Security</MenuItem>
          <MenuItem value="QEC">QEC</MenuItem>

          
        </Select>
        </FormControl>
      </Box>

      {filteredQuestions.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          bgcolor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            All Caught Up! 🎉
          </Typography>
          <Typography variant="body1" sx={{ color: '#7f8c8d' }}>
            No questions are available for approval right now.
          </Typography>
        </Box>
      )}

      {/* Question List */}
      {filteredQuestions.map(q => (
        <Box key={q._id} sx={{ 
          bgcolor: 'white',
          border: '1px solid #e0e0e0', 
          borderRadius: 3, 
          p: 3, 
          mb: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            transform: 'translateY(-2px)',
          },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                {q.subject}
              </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d' }}>{q.teacherName ? `By ${q.teacherName}` : ''}</Typography>
                  <Typography variant="caption" sx={{ color: '#7f8c8d' }}>{q.dateCreated ? new Date(q.dateCreated).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric'}) : ''}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    {q.level !== undefined && (
                      <Box sx={{ bgcolor: '#eef2ff', color: '#333', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>
                        Level {q.level}
                      </Box>
                    )}
                    {q.status && (
                      <Box sx={{ bgcolor: '#fff4e6', color: '#333', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>
                        {q.status === 'review_needed' ? 'Review Needed' : q.status.charAt(0).toUpperCase() + q.status.slice(1)}
                      </Box>
                    )}
                  </Box>
                </Box>
              <Box
                sx={{
                  backgroundColor:
                    q.qaDecision === 'approved'
                      ? '#4caf50'
                      : q.qaDecision === 'rejected'
                      ? '#f44336'
                      : '#ff9800',
                  borderRadius: 2,
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {q.qaDecision === 'approved'
                  ? '✓ Marked Approved'
                  : q.qaDecision === 'rejected'
                  ? '✗ Marked Rejected'
                  : 'Assigned'}
              </Box>
            </Box>
            {['approved', 'rejected'].includes(q.qaDecision) && (
              <Typography variant="caption" sx={{ color: '#7f8c8d', fontStyle: 'italic' }}>
                (Awaiting Submit)
              </Typography>
            )}
          </Box>

          <Box sx={{ 
            p: 2, 
            bgcolor: '#f8f9fa', 
            borderRadius: 2,
            mb: 2,
            border: '1px solid #e0e0e0',
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
          }} 
            dangerouslySetInnerHTML={{ __html: q.question }} 
          />

          {q.subQuestions?.length > 0 && (
            <Box sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: '#e3f2fd',
              px: 2,
              py: 0.75,
              borderRadius: 2,
              mb: 2,
            }}>
              <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                {q.subQuestions.length} Sub Question{q.subQuestions.length > 1 ? 's' : ''} Added
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button 
              variant="outlined" 
              onClick={() => handleView(q)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              View Details
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              onClick={() => markApproved(q._id)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Approve
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={() => markRejected(q._id)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Reject
            </Button>
            <Button 
              variant="outlined" 
              color="warning" 
              onClick={() => handleSendBackClick(q)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Send Back
            </Button>
            <Button 
              variant="outlined" 
              color="info" 
              onClick={() => handleEditClick(q)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Edit
            </Button>
            {q.qaDecision !== 'none' && (
              <Button 
                variant="outlined" 
                onClick={() => markNoDecision(q._id)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Revert
              </Button>
            )}
          </Box>
        </Box>
      ))}

      {filteredQuestions.length !== 0 && (
        <Button 
          variant="contained" 
          fullWidth
          onClick={handleSaveAll}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            fontSize: '1rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Save All Decisions
        </Button>
      )}

      {/* View Dialog */}
      <Dialog 
        open={viewOpen} 
        onClose={handleCloseView} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Question Details
        </DialogTitle>
        {viewedQuestion && (
          <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
            {/* Subject Card */}
            <Box sx={{ 
              bgcolor: 'white', 
              p: 2.5, 
              borderRadius: 2,
              borderLeft: '4px solid #667eea',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              mb: 2,
            }}>
              <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                Subject
              </Typography>
              <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 700 }}>
                {viewedQuestion.subject}
              </Typography>
            </Box>

            {/* Question Text */}
            <Box sx={{ 
              bgcolor: 'white', 
              p: 3, 
              borderRadius: 2,
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Question
              </Typography>
              <Box
                sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: viewedQuestion.question }}
              />
            </Box>

            {/* Options */}
            <Box sx={{ 
              bgcolor: 'white', 
              p: 3, 
              borderRadius: 2,
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Answer Options
              </Typography>
              <Grid container spacing={2}>
                {viewedQuestion.options.map((opt, i) => {
                  const isCorrect = opt === viewedQuestion.correctAnswer;
                  return (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                          bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ 
                          minWidth: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          flexShrink: 0,
                        }}>
                          {String.fromCharCode(65 + i)}
                        </Box>
                        <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                          {isCorrect && (
                            <Typography variant="caption" sx={{ 
                              display: 'block',
                              color: '#4caf50',
                              fontWeight: 700,
                              mb: 0.5,
                            }}>
                              ✓ Correct Answer
                            </Typography>
                          )}
                          <Box dangerouslySetInnerHTML={{ __html: opt }} />
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Sub Questions Button */}
            {viewedQuestion.subQuestions?.length > 0 && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setOpenSubModal(true)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View {viewedQuestion.subQuestions.length} Sub Question{viewedQuestion.subQuestions.length > 1 ? 's' : ''}
              </Button>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={handleCloseView}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub Questions Modal (view mode) */}
      <Dialog 
        open={openSubModal} 
        onClose={() => setOpenSubModal(false)} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Sub Questions ({viewedQuestion?.subQuestions.length || 0})
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
          {viewedQuestion?.subQuestions.map((sq, idx) => (
            <Box 
              key={idx} 
              sx={{ 
                bgcolor: 'white',
                borderRadius: 2,
                p: 3,
                mb: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #11998e',
              }}
            >
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                color: '#2c3e50',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Box sx={{
                  bgcolor: '#11998e',
                  color: 'white',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                }}>
                  {idx + 1}
                </Box>
                Sub Question {idx + 1}
              </Typography>
              
              <Box
                sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: sq.question }}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1.5 }}>
                Answer Options
              </Typography>
              <Grid container spacing={2}>
                {sq.options.map((opt, i) => {
                  const isCorrect = opt === sq.correctAnswer;
                  return (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                          bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ 
                          minWidth: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          flexShrink: 0,
                        }}>
                          {String.fromCharCode(65 + i)}
                        </Box>
                        <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                          {isCorrect && (
                            <Typography variant="caption" sx={{ 
                              display: 'block',
                              color: '#4caf50',
                              fontWeight: 700,
                              mb: 0.5,
                            }}>
                              ✓ Correct Answer
                            </Typography>
                          )}
                          <Box dangerouslySetInnerHTML={{ __html: opt }} />
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={() => setOpenSubModal(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Back Dialog */}
      <Dialog open={sendBackOpen} onClose={handleSendBackClose} fullWidth maxWidth="sm">
        <DialogTitle>Send Back Question</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a comment or reason for sending this question back to the teacher.
          </Typography>
          <TextField
            fullWidth multiline minRows={3}
            value={sendBackComment}
            onChange={e => setSendBackComment(e.target.value)}
            placeholder="e.g. Please fix grammar in option C."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSendBackClose}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSendBackConfirm}>
            Send Back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth="lg">
        {editQuestion && (
          <>
            {/* Dialog Header */}
            <Box sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              p: 3,
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                Edit Question
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Modify question content and sub-questions
              </Typography>
            </Box>

            <DialogContent dividers sx={{ p: 4, bgcolor: '#fafafa' }}>
              {/* Question Content */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 6, height: 24, bgcolor: '#f5576c', borderRadius: 1 }} />
                  Question Content
                </Typography>
                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '2px solid #e0e0e0',
                }}>
                  <CustomEditor
                    key={`edit-question-${editQuestion?._id}`}
                    value={editQuestionText}
                    onChange={setEditQuestionText}
                  />
                </Box>
              </Box>

              {/* Parent Question Options (editable) */}
              {(!editSubQuestions || editSubQuestions.length === 0) && editParentOptions.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 6, height: 24, bgcolor: '#f5576c', borderRadius: 1 }} />
                    Answer Options
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
                    Edit the option text below and select the correct answer using the radio buttons.
                  </Typography>
                  <RadioGroup
                    value={editParentOptions.findIndex(opt => opt === editParentCorrectAnswer)}
                    onChange={e => {
                      const idx = Number(e.target.value);
                      setEditParentCorrectAnswer(editParentOptions[idx]);
                    }}
                  >
                    <Grid container spacing={2}>
                      {editParentOptions.map((opt, i) => (
                        <Grid item xs={12} key={i}>
                          <Box sx={{
                            p: 2.5,
                            bgcolor: editParentCorrectAnswer === opt ? '#e8f5e9' : '#fafafa',
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: editParentCorrectAnswer === opt ? '#4caf50' : '#e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}>
                              <FormControlLabel control={<MuiRadio />} value={i} label="" sx={{ m: 0 }} />
                              <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: editParentCorrectAnswer === opt ? '#4caf50' : '#9e9e9e',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: '1.2rem',
                              }}>
                                {String.fromCharCode(65 + i)}
                              </Box>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <CustomEditor
                                key={`edit-parent-opt-${i}`}
                                value={opt}
                                onChange={val => {
                                  const oldOpt = editParentOptions[i];
                                  const newOpts = [...editParentOptions];
                                  newOpts[i] = val;
                                  setEditParentOptions(newOpts);
                                  if (editParentCorrectAnswer === oldOpt) {
                                    setEditParentCorrectAnswer(val);
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>
                </Box>
              )}

              {/* Sub Questions */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 6, height: 24, bgcolor: '#f5576c', borderRadius: 1 }} />
                    Sub Questions {editSubQuestions.length > 0 && `(${editSubQuestions.length})`}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setEditSubModalOpen(true);
                      setSubQuestionTempEdit({ question: '', options: ['', '', '', ''], correctOptionIndex: 0 });
                    }}
                    sx={{
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    + Add Sub Question
                  </Button>
                </Box>

                {editSubQuestions.length === 0 ? (
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: 'white', 
                    borderRadius: 2,
                    border: '2px dashed #e0e0e0',
                    textAlign: 'center',
                  }}>
                    <Typography variant="body2" sx={{ color: '#7f8c8d', fontStyle: 'italic' }}>
                      No sub questions added yet.
                    </Typography>
                  </Box>
                ) : (
                  editSubQuestions.map((sq, idx) => (
                    <Box key={idx} sx={{ 
                      p: 3, 
                      mb: 2,
                      bgcolor: 'white',
                      borderRadius: 2, 
                      border: '2px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#11998e',
                        boxShadow: '0 4px 12px rgba(17, 153, 142, 0.15)',
                      },
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: '#11998e',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                          }}>
                            {idx + 1}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                            Sub Question {idx + 1}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setEditSubQuestions(editSubQuestions.filter((_, j) => j !== idx));
                          }}
                          sx={{ borderRadius: 2, fontWeight: 600 }}
                        >
                          Delete
                        </Button>
                      </Box>
                      
                      <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                        <CustomEditor
                          key={`edit-subq-${idx}-${subQuestionEditResetCounter}`}
                          value={sq.question}
                          onChange={val => {
                            const updated = [...editSubQuestions];
                            updated[idx].question = val;
                            setEditSubQuestions(updated);
                          }}
                        />
                      </Box>

                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
                        Answer Options
                      </Typography>
                      <RadioGroup
                        value={sq.options.findIndex(opt => opt === sq.correctAnswer)}
                        onChange={e => {
                          const index = Number(e.target.value);
                          const updated = [...editSubQuestions];
                          updated[idx].correctAnswer = updated[idx].options[index];
                          setEditSubQuestions(updated);
                        }}
                      >
                        <Grid container spacing={1.5}>
                          {sq.options.map((opt, i) => (
                            <Grid item xs={12} key={i}>
                              <Box sx={{
                                p: 2,
                                bgcolor: sq.correctAnswer === opt ? '#e8f5e9' : '#fafafa',
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: sq.correctAnswer === opt ? '#4caf50' : '#e0e0e0',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: sq.correctAnswer === opt ? '#4caf50' : '#11998e',
                                },
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <FormControlLabel 
                                    control={<MuiRadio />} 
                                    value={i} 
                                    label=""
                                    sx={{ m: 0, mr: 1.5 }}
                                  />
                                  <Box sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: sq.correctAnswer === opt ? '#4caf50' : '#9e9e9e',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                  }}>
                                    {String.fromCharCode(65 + i)}
                                  </Box>
                                  <Typography variant="subtitle2" sx={{ ml: 1.5, fontWeight: 600, color: '#2c3e50' }}>
                                    Option {i + 1}
                                  </Typography>
                                </Box>
                                <Box sx={{ pl: 5.5 }}>
                                  <CustomEditor
                                    key={`edit-subq-opt-${i}-${subQuestionEditResetCounter}`}
                                    value={opt}
                                    onChange={newVal => {
                                      const updated = [...editSubQuestions];
                                      const oldOpt = updated[idx].options[i];
                                      updated[idx].options[i] = newVal;
                                      if (updated[idx].correctAnswer === oldOpt) {
                                        updated[idx].correctAnswer = newVal;
                                      }
                                      setEditSubQuestions(updated);
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </RadioGroup>
                    </Box>
                  ))
                )}
              </Box>
            </DialogContent>
          </>
        )}

        <DialogActions sx={{ p: 2.5, bgcolor: '#fafafa', borderTop: '2px solid #e0e0e0' }}>
          <Button 
            onClick={handleEditClose}
            sx={{ 
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditSave}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e07fea 0%, #e4455a 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(245, 87, 108, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Sub-Question Modal in Edit */}
      <Dialog open={editSubModalOpen} onClose={() => setEditSubModalOpen(false)} fullWidth maxWidth="md">
        <Box sx={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          p: 3,
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
            Add New Sub Question
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Create a sub-question with multiple choice options
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 4, bgcolor: '#fafafa' }}>
          {/* Sub-question Text */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
              Sub-Question Text
            </Typography>
            <Box sx={{ 
              p: 3, 
              bgcolor: 'white', 
              borderRadius: 2, 
              border: '2px solid #e0e0e0',
            }}>
              <CustomEditor
                key={`edit-subq-temp-${subQuestionEditResetCounter}`}
                value={subQuestionTempEdit.question}
                onChange={val => setSubQuestionTempEdit(prev => ({ ...prev, question: val }))}
              />
            </Box>
          </Box>

          {/* Options */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
              Answer Options
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
              Select the correct answer by clicking the radio button
            </Typography>
            <Grid container spacing={2}>
              {subQuestionTempEdit.options.map((opt, idx) => (
                <Grid item xs={12} key={idx}>
                  <Box sx={{
                    p: 2.5,
                    bgcolor: subQuestionTempEdit.correctOptionIndex === idx ? '#e8f5e9' : 'white',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: subQuestionTempEdit.correctOptionIndex === idx ? '#4caf50' : '#e0e0e0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: subQuestionTempEdit.correctOptionIndex === idx ? '#4caf50' : '#11998e',
                      transform: 'translateX(4px)',
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <FormControlLabel
                        control={
                          <MuiRadio
                            checked={subQuestionTempEdit.correctOptionIndex === idx}
                            onChange={() => setSubQuestionTempEdit(prev => ({ ...prev, correctOptionIndex: idx }))}
                          />
                        }
                        label=""
                        sx={{ m: 0, mr: 2 }}
                      />
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: subQuestionTempEdit.correctOptionIndex === idx ? '#4caf50' : '#9e9e9e',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}>
                        {String.fromCharCode(65 + idx)}
                      </Box>
                      <Typography variant="subtitle2" sx={{ ml: 2, fontWeight: 700, color: '#2c3e50' }}>
                        Option {idx + 1}
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 7 }}>
                      <CustomEditor
                        key={`edit-subq-opt-temp-${idx}-${subQuestionEditResetCounter}`}
                        value={opt}
                        onChange={newVal => {
                          const newOpts = [...subQuestionTempEdit.options];
                          newOpts[idx] = newVal;
                          setSubQuestionTempEdit(prev => ({ ...prev, options: newOpts }));
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#fafafa', borderTop: '2px solid #e0e0e0' }}>
          <Button 
            onClick={() => {
              setEditSubModalOpen(false);
              setSubQuestionTempEdit({ question: '', options: ['', '', '', ''], correctOptionIndex: 0 });
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddSubQuestionEdit}
            sx={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Save Sub Question
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default QAPage;
